'use client';

import { ReactNode, useEffect, useMemo } from 'react';

import { EMBED_SESSION_STORAGE_KEY, EMBED_VID_STORAGE_KEY } from '@/helpers/embed';
import { VisitorCookie } from '@/models';
import { persistFirstLoadWithIdentity, persistVisitorWithIdentity } from '@/server/loadVisitorSsr';

import { setEmbedClientIdentity } from './apiHeaders';
import { PersistStrategy, VisitorProvider } from './VisitorProvider';

type Identity = { sessionId: string; vid: string };

type Props = {
  children: ReactNode;
  sessionId: string;
  vid: string;
  visitorCookie?: VisitorCookie;
};

export const createEmbedPersist = ({
  geolocation,
  sessionId,
  vid,
}: Identity & { geolocation?: VisitorCookie['geolocation'] }): PersistStrategy => ({
  persistFirstLoad: (meta) => persistFirstLoadWithIdentity(meta, { sessionId, vid }, geolocation),
  persistVisitor: (updates) => persistVisitorWithIdentity(updates, { sessionId, vid }),
});

const SeedEmbedClientIdentity = ({ sessionId, vid }: Identity) => {
  useEffect(() => {
    setEmbedClientIdentity({ sessionId, vid });
    try {
      sessionStorage.setItem(EMBED_VID_STORAGE_KEY, vid);
      sessionStorage.setItem(EMBED_SESSION_STORAGE_KEY, sessionId);
    } catch {
      // Storage blocked; memory identity still serves ambient fetches
    }

    return () => {
      setEmbedClientIdentity(null);
      try {
        sessionStorage.removeItem(EMBED_VID_STORAGE_KEY);
        sessionStorage.removeItem(EMBED_SESSION_STORAGE_KEY);
      } catch {
        // Storage blocked
      }
    };
  }, [sessionId, vid]);

  return null;
};

// Client gate so PersistStrategy (functions) is built on the client — RSC cannot pass functions as props.
export const EmbedVisitorProvider = ({ children, sessionId, vid, visitorCookie }: Props) => {
  const persist = useMemo(
    () => createEmbedPersist({ geolocation: visitorCookie?.geolocation, sessionId, vid }),
    [sessionId, vid, visitorCookie?.geolocation]
  );

  return (
    <>
      <SeedEmbedClientIdentity sessionId={sessionId} vid={vid} />
      <VisitorProvider
        persist={persist}
        reserveConciergePhone={false}
        visitorCookie={visitorCookie}
        writeCookies={false}
      >
        {children}
      </VisitorProvider>
    </>
  );
};
